import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatPrice, getStatusLabel } from "@/lib/api";

/**
 * Receipt print page — auto-prints on load.
 * Designed for 80mm thermal printers but works with any installed printer.
 * The user just prints to their thermal printer (set as default in OS).
 */
export default function OrderReceiptPrint() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/admin/orders/${id}`).then(r => setOrder(r.data)).catch(e => setError(e.response?.data?.detail || "Заказ не найден"));
  }, [id]);

  useEffect(() => {
    if (order) {
      // small delay so DOM paints first
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [order]);

  if (error) return <div style={{ padding: 20 }}>{error}</div>;
  if (!order) return <div style={{ padding: 20 }}>Загрузка...</div>;

  const date = new Date(order.created_at);
  const dt = date.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  // Group items by type for clarity
  const menuItems = (order.items || []).filter(i => (i.item_type || "menu") === "menu");
  const serviceItems = (order.items || []).filter(i => i.item_type === "service");

  return (
    <>
      <style>{`
        @page { size: 80mm auto; margin: 0; }
        @media print {
          body { margin: 0 !important; background: white !important; }
          .no-print { display: none !important; }
          .receipt { box-shadow: none !important; padding: 4mm !important; }
        }
        body { background: #f0f0f0; font-family: 'Courier New', Courier, monospace; }
        .receipt {
          width: 72mm; padding: 6mm 4mm; background: white; color: #000;
          margin: 20px auto; box-shadow: 0 4px 24px rgba(0,0,0,0.1);
          font-size: 12px; line-height: 1.4;
        }
        .receipt h1 { font-size: 14px; margin: 0 0 2px; text-align: center; letter-spacing: 1px; }
        .receipt .sub { text-align: center; font-size: 11px; margin-bottom: 6px; }
        .receipt hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
        .receipt .row { display: flex; justify-content: space-between; gap: 8px; }
        .receipt .item { margin-bottom: 4px; }
        .receipt .item .name { font-weight: bold; }
        .receipt .item .meta { display: flex; justify-content: space-between; font-size: 11px; }
        .receipt .pkg-tag { font-size: 10px; color: #555; font-style: italic; }
        .receipt .total { font-size: 14px; font-weight: bold; margin-top: 4px; }
        .receipt .footer { text-align: center; margin-top: 8px; font-size: 11px; }
        .toolbar { text-align: center; margin: 12px 0; }
        .toolbar button { padding: 8px 16px; margin: 0 4px; cursor: pointer; border: 1px solid #888; background: #fff; }
      `}</style>

      <div className="toolbar no-print">
        <button onClick={() => window.print()} data-testid="print-btn">🖨 Печать</button>
        <button onClick={() => window.close()}>Закрыть</button>
      </div>

      <div className="receipt" data-testid="receipt">
        <h1>ECO VILLAGE KZO</h1>
        <div className="sub">Банный комплекс</div>

        <hr />

        <div className="row"><span>Заказ №</span><span>{order.id.slice(0, 8).toUpperCase()}</span></div>
        <div className="row"><span>Дата</span><span>{dt}</span></div>
        <div className="row"><span>Место</span><span>{order.venue_name || "—"}</span></div>
        <div className="row"><span>Статус</span><span>{getStatusLabel(order.status)}</span></div>
        {order.client_name && <div className="row"><span>Клиент</span><span>{order.client_name}</span></div>}
        {order.client_phone && <div className="row"><span>Тел.</span><span>{order.client_phone}</span></div>}

        {menuItems.length > 0 && (
          <>
            <hr />
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>КУХНЯ И БАР</div>
            {menuItems.map((it, i) => (
              <div key={i} className="item">
                <div className="name">{it.name || "—"}</div>
                {it.package_name_ru && <div className="pkg-tag">в составе: {it.package_name_ru}</div>}
                <div className="meta">
                  <span>{it.quantity} × {formatPrice(it.price)}</span>
                  <span>{formatPrice((it.price || 0) * (it.quantity || 1))}</span>
                </div>
              </div>
            ))}
          </>
        )}

        {serviceItems.length > 0 && (
          <>
            <hr />
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>УСЛУГИ</div>
            {serviceItems.map((it, i) => (
              <div key={i} className="item">
                <div className="name">{it.name || "—"}</div>
                {it.package_name_ru && <div className="pkg-tag">в составе: {it.package_name_ru}</div>}
                <div className="meta">
                  <span>{it.quantity} × {formatPrice(it.price)}</span>
                  <span>{formatPrice((it.price || 0) * (it.quantity || 1))}</span>
                </div>
              </div>
            ))}
          </>
        )}

        {order.notes && (
          <>
            <hr />
            <div style={{ fontSize: 11 }}><b>Примечание:</b> {order.notes}</div>
          </>
        )}

        <hr />
        <div className="row total">
          <span>ИТОГО</span>
          <span>{formatPrice(order.total_amount)}</span>
        </div>

        <hr />
        <div className="footer">
          Спасибо за заказ!<br />
          ecovillage-kzo.kz
        </div>
      </div>
    </>
  );
}
